import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import api from './api/axiosInstance';
import Modal from 'react-bootstrap/Modal';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { FaFileAlt, FaDownload } from 'react-icons/fa';
import base_url from './api/Config';
import TopBar from './component/TopBar';

const MANDATE_LABELS = {
  NOT_CREATED: { text: 'Not set up', color: '#888' },
  CREATED: { text: 'Awaiting your bank authorisation', color: '#e6a700' },
  AUTHORIZED: { text: 'Confirming with your bank…', color: '#e6a700' },
  CONFIRMED: { text: 'Active', color: '#2e9e44' },
  REJECTED: { text: 'Rejected by bank — please retry', color: '#e5393e' },
  CANCELLED: { text: 'Cancelled', color: '#888' },
  FAILED: { text: 'Failed — please retry', color: '#e5393e' },
};

const LoanDetails = () => {
  const [loanDetails, setLoanDetails] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [repaymentDetails, setRepaymentDetails] = useState(null);
  const [loanStatement, setLoanStatement] = useState(null);
  const [showLoanAgreementModal, setShowLoanAgreementModal] = useState(false);
  const [showKFSModal, setShowKFSModal] = useState(false);
  const [showLoanStatementModal, setShowLoanStatementModal] = useState(false);
  const [mandateStatus, setMandateStatus] = useState(null);
  // null when idle, otherwise the authType ('netbanking' | 'upi' | 'card') being set up
  const [mandateBusy, setMandateBusy] = useState(null);
  const [mandateError, setMandateError] = useState('');

  const location = useLocation();
  const id = new URLSearchParams(location.search).get('id');
  const printRef = useRef(null);
  const mandatePollRef = useRef(null);

  // Registration link is a hosted Razorpay page opened in the system browser —
  // there's no in-page handler callback, so we poll our own status endpoint
  // until the mandate reaches a final state (~5 minutes max).
  const MANDATE_POLL_INTERVAL_MS = 3000;
  const MANDATE_POLL_MAX_ATTEMPTS = 100;

  const stopMandatePolling = () => {
    if (mandatePollRef.current) {
      clearInterval(mandatePollRef.current);
      mandatePollRef.current = null;
    }
  };

  const fetchMandateStatus = useCallback(() => {
    if (!id) return Promise.resolve(null);
    return api.get(`${base_url}/api/razorpay-mandate/status/${id}`)
      .then(res => { setMandateStatus(res.data); return res.data; })
      .catch(() => null);
  }, [id]);

  const pollMandateStatus = () => {
    stopMandatePolling();
    let attempts = 0;
    mandatePollRef.current = setInterval(async () => {
      attempts += 1;
      const data = await fetchMandateStatus();
      const status = data?.status;
      if (status === 'CONFIRMED') {
        stopMandatePolling();
        setMandateBusy(null);
        return;
      }
      if (['REJECTED', 'CANCELLED', 'FAILED'].includes(status)) {
        stopMandatePolling();
        setMandateBusy(null);
        setMandateError('Bank authorisation was declined or the link expired. Please try again.');
        return;
      }
      if (attempts >= MANDATE_POLL_MAX_ATTEMPTS) {
        stopMandatePolling();
        setMandateBusy(null);
      }
    }, MANDATE_POLL_INTERVAL_MS);
  };

  // Resume polling automatically if a mandate was already mid-flight
  // (e.g. the customer left this page open, or came back to it later).
  useEffect(() => {
    fetchMandateStatus().then(data => {
      if (data && ['CREATED', 'AUTHORIZED'].includes(data.status)) {
        setMandateBusy(data.authType || 'netbanking');
        pollMandateStatus();
      }
    });
    return () => stopMandatePolling();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // authType: 'netbanking' | 'upi' | 'card' — picked by the customer below.
  const handleSetupMandate = async (authType) => {
    if (!id) return;
    setMandateError('');
    setMandateBusy(authType);
    try {
      const createRes = await api.post(`${base_url}/api/razorpay-mandate/create`, { loanId: id, authType });
      const data = createRes.data;
      if (!data || !data.paymentLinkUrl) {
        throw new Error(data?.error || 'Could not start auto-debit setup.');
      }
      window.open(data.paymentLinkUrl, '_blank');
      pollMandateStatus();
    } catch (e) {
      setMandateError(e.message || 'Could not start auto-debit setup.');
      setMandateBusy(null);
    }
  };

  useEffect(() => {
    if (!id) return;
    api.get(`${base_url}/api/user/getApplicationDetailsById`, { params: { id } })
      .then(res => setUserDetails(res.data))
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!id) return;
    api.get(`${base_url}/api/userProduct/application?id=${id}`)
      .then(res => setLoanDetails(res.data))
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!id) return;
    api.get(`${base_url}/api/keyFact/keyFactByAppId?id=${id}`)
      .then(res => setLoanStatement(res.data))
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!id) return;
    api.get(`${base_url}/api/approveRejectLoan/getRepaymentByAppList?applicationId=${id}`)
      .then(res => setRepaymentDetails(res.data))
      .catch(() => {});
  }, [id]);

  const closeAll = () => {
    setShowLoanAgreementModal(false);
    setShowKFSModal(false);
    setShowLoanStatementModal(false);
  };

  const generatePdf = async (filename) => {
    const input = printRef.current;
    if (!input) return;
    const origBg = input.style.backgroundColor;
    const origFs = input.style.fontSize;
    input.style.backgroundColor = 'transparent';
    input.style.fontSize = '8px';
    const canvas = await html2canvas(input, { scale: 2 });
    const imgData = canvas.toDataURL('image/jpeg', 1);
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const imgW = pageW - margin * 2;
    const imgH = (canvas.height * imgW) / canvas.width;
    let heightLeft = imgH;
    let position = margin;
    pdf.addImage(imgData, 'JPEG', margin, position, imgW, imgH);
    heightLeft -= pageH - margin;
    while (heightLeft > 0) {
      position = margin - heightLeft;
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', margin, position, imgW, imgH);
      heightLeft -= pageH;
    }
    pdf.save(filename, { returnPromise: true }).then(() => {
      input.style.backgroundColor = origBg;
      input.style.fontSize = origFs;
    });
  };

  const fmtDate = (d) => d ? new Date(d).toISOString().split('T')[0] : '';

  const docItems = [
    { label: 'Key Fact Statement (KFS)', onPress: () => setShowKFSModal(true) },
    { label: 'Loan Agreement',           onPress: () => setShowLoanAgreementModal(true) },
    { label: 'Loan Statement',           onPress: () => setShowLoanStatementModal(true) },
  ];

  return (
    <div className="app-page">
      <TopBar title="Loan Details" backPath={`/CustomerEmiPage?id=${id}`} />

      <div className="app-content">
        <div className="app-card">
          <p className="section-title">Loan ID: {id}</p>
          {loanDetails && (
            <>
              <div className="info-row">
                <span>Loan Amount</span>
                <strong>₹{loanDetails.loanAmount} /-</strong>
              </div>
              <div className="info-row">
                <span>Status</span>
                <span className="status-badge">{loanDetails.status}</span>
              </div>
            </>
          )}
        </div>

        <div className="app-card">
          <p className="section-title">Auto-Debit Mandate (NACH)</p>
          <div className="info-row">
            <span>Status</span>
            <span className="status-badge" style={{ color: MANDATE_LABELS[mandateStatus?.status]?.color || '#888' }}>
              {MANDATE_LABELS[mandateStatus?.status]?.text || 'Not set up'}
            </span>
          </div>
          {mandateError && (
            <p style={{ color: '#e5393e', fontSize: 12, marginTop: 6, marginBottom: 6 }}>{mandateError}</p>
          )}
          {(!mandateStatus || ['NOT_CREATED', 'REJECTED', 'CANCELLED', 'FAILED'].includes(mandateStatus.status)) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
              <button className="btn btn-primary" disabled={!!mandateBusy} onClick={() => handleSetupMandate('netbanking')}>
                {mandateBusy === 'netbanking' ? 'Please wait…' : '🏦 Setup via Netbanking'}
              </button>
              <button className="btn btn-primary" disabled={!!mandateBusy} onClick={() => handleSetupMandate('upi')}>
                {mandateBusy === 'upi' ? 'Please wait…' : '📱 Setup via UPI Autopay'}
              </button>
              <button className="btn btn-primary" disabled={!!mandateBusy} onClick={() => handleSetupMandate('card')}>
                {mandateBusy === 'card' ? 'Please wait…' : '💳 Setup via Card'}
              </button>
            </div>
          )}
          {mandateStatus && ['CREATED', 'AUTHORIZED'].includes(mandateStatus.status) && (
            <>
              <p style={{ fontSize: 12, color: '#888', marginTop: 6, marginBottom: 6 }}>
                Checking automatically… complete the authorisation in the browser tab if it's still open.
              </p>
              <button
                className="btn btn-secondary"
                style={{ marginTop: 4, width: '100%' }}
                onClick={() => { setMandateBusy(mandateStatus.authType || 'netbanking'); pollMandateStatus(); }}
              >
                Refresh Status
              </button>
            </>
          )}
        </div>

        <div className="app-card">
          <p className="section-title">Documents</p>
          {docItems.map((doc, i) => (
            <div
              key={i}
              className="list-card"
              style={{ cursor: 'pointer', marginBottom: i < docItems.length - 1 ? 8 : 0 }}
              onClick={doc.onPress}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <FaFileAlt style={{ color: '#1d2e77' }} />
                <span style={{ fontWeight: 600, fontSize: 14 }}>{doc.label}</span>
              </div>
              <span style={{ fontSize: 12, color: '#1d2e77' }}>View</span>
            </div>
          ))}
        </div>
      </div>

      {/* Loan Agreement Modal */}
      <Modal show={showLoanAgreementModal} onHide={closeAll} backdrop="static" keyboard={false} size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16 }}>Loan Agreement</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto', fontSize: 12 }}>
          <div ref={printRef} style={{ padding: 8 }}>
            <h5 style={{ textAlign: 'center', textDecoration: 'underline' }}>Loan Agreement</h5>
            <p>This Loan Agreement is made on this Saturday of 27 Jul, 2024, at Haryana, India.</p>
            <p><strong>By and Between</strong></p>
            <p>Ujjawal Srijan, a non-banking finance company registered with the Reserve Bank of India ("RBI"), having its registered office at 1st Floor, Commerz II, Goregaon East, Mumbai, Maharashtra, India, 400063 (the "Lender").</p>
            <p>Mr/Ms/Mrs. <strong>{userDetails?.name || ''}</strong>, bearing PAN <strong>{userDetails?.panNo || ''}</strong>, son/daughter of <strong>{loanDetails?.fatherName || ''}</strong> (the "Borrower").</p>
            <p>This Loan is facilitated through digital lending application "Brand Name" developed and owned by the Fintech Name, acting as a Lending Service Provider for the Lender.</p>

            <p><strong>Loan:</strong> Subject to the terms herein, We have agreed to make available a term loan of amounts not exceeding those specified in the Key Fact Statement (KFS).</p>
            <p><strong>Interest:</strong> The Interest rate as detailed in the KFS ("APR") shall be payable along with the principal repayment instalments.</p>
            <p><strong>Processing Fee:</strong> Processing Fee as specified in the KFS will be charged and is non-refundable after one day from the disbursement date.</p>

            <p><strong>REPAYMENT</strong></p>
            <p>You are required to pay the total Outstanding Amount as specified in the Repayment Schedule. On each Due Date, We may debit Your registered bank account via NACH mandate or other payment modes acceptable to Us.</p>
            <p>Late Payment Charges as set out in the KFS will apply if the Instalment Amount is not available on a Due Date.</p>
            <p>Missing payments could have severe consequences, including civil and/or criminal legal proceedings. We report missed payments to credit bureaus.</p>

            <p><strong>Terms & Conditions</strong></p>
            <p>3.1 Loan Approval is subject to Our internal credit approval, the availability of funds, compliance with applicable laws, and submission of KYC documents.</p>
            <p>3.3 Pre-Payment: You may prepay the loan after the Cooling-off period subject to applicable Prepayment Charges.</p>
            <p>4. Default: We will demand immediate repayment if You delay payment, provide incorrect information, breach these Loan Terms, or become bankrupt/insolvent.</p>

            <p><strong>Use of Personal Information</strong></p>
            <p>You agree and acknowledge that We may use data that identifies You for statistical analysis, fraud prevention, regulatory checks, and keeping You informed about Your Loan.</p>

            <p><strong>Grievance Redressal</strong></p>
            <p>In case of any grievances against Us, you may lodge a complaint by phone, in writing, or through electronic means as specified on our website.</p>

            <p><strong>General</strong></p>
            <p>These Loan Terms are governed by Indian law, and You submit to the non-exclusive jurisdiction of the courts in Mumbai.</p>

            <p style={{ textAlign: 'center', marginTop: 32 }}>Annexure — Repayment Schedule</p>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginTop: 8 }}>
              <thead>
                <tr style={{ background: '#f0f0f0' }}>
                  <th style={{ border: '1px solid #ddd', padding: 4 }}>Due Date</th>
                  <th style={{ border: '1px solid #ddd', padding: 4 }}>EMI (INR)</th>
                  <th style={{ border: '1px solid #ddd', padding: 4 }}>Principal Due</th>
                  <th style={{ border: '1px solid #ddd', padding: 4 }}>Interest Due</th>
                  <th style={{ border: '1px solid #ddd', padding: 4 }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {repaymentDetails?.length > 0 ? repaymentDetails.map((r, i) => (
                  <tr key={i}>
                    <td style={{ border: '1px solid #ddd', padding: 4 }}>{r.dueDate || '—'}</td>
                    <td style={{ border: '1px solid #ddd', padding: 4 }}>{r.emiAmount || '—'}</td>
                    <td style={{ border: '1px solid #ddd', padding: 4 }}>{r.principal || '—'}</td>
                    <td style={{ border: '1px solid #ddd', padding: 4 }}>{r.interestRate || '—'}</td>
                    <td style={{ border: '1px solid #ddd', padding: 4 }}>{r.remainingAmount || '—'}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={5} style={{ border: '1px solid #ddd', padding: 8, textAlign: 'center' }}>No repayment data</td>
                  </tr>
                )}
              </tbody>
            </table>

            <p style={{ marginTop: 32 }}>Signature of Borrower</p>
            <p>By clicking "Review and Proceed" you electronically sign and accept these Loan Terms including the Sanction Letter, T&C, and KFS.</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={closeAll}>Close</button>
          <button className="btn btn-primary" onClick={() => generatePdf('Loan_agreement.pdf')}>
            Download <FaDownload />
          </button>
        </Modal.Footer>
      </Modal>

      {/* KFS Modal */}
      <Modal show={showKFSModal} onHide={closeAll} backdrop="static" keyboard={false} size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16 }}>Key Fact Statement</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto', fontSize: 12 }}>
          <div ref={printRef} style={{ padding: 8 }}>
            <h5 style={{ textAlign: 'center', textDecoration: 'underline' }}>Key Fact Statement</h5>
            <p>Dear {userDetails?.name || 'Borrower'},</p>
            <p>Please refer to your application for the credit facility from Lender made through the digital lending application by Fintech Name.</p>

            <p><strong>A. Payment Related Terms</strong></p>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <tbody>
                {[
                  ['1) Loan Term', `${loanDetails?.tenure || ''} Days`],
                  ['2) Loan Amount', `₹${loanDetails?.loanAmount || ''}`],
                  ['3a) Processing Fee', `₹${loanDetails?.processFee || ''}`],
                  ['3b) Other Charges', 'NA'],
                  ['4) Net Disbursed Amount', `₹${loanDetails?.disbursementAmount || ''}`],
                  ['5) Total Interest Payable', `₹${loanDetails?.interestAmount || ''}`],
                  ['6) APR', `${loanDetails?.apr || ''}%`],
                  ['8) No. of Instalments', loanDetails?.noOfInstalment || ''],
                  ['11) Lock-in Period', '3 Days'],
                  ['12) Prepayment Charges', '1% of principal or ₹250 (whichever is higher, GST applicable)'],
                  ['13) Late Payment Charges', '₹300 flat + 36% p.a. on overdue amount (GST applicable)'],
                  ['14) Other Charges', 'NA'],
                  ['15) Recovery Agency', 'Fintech Name'],
                  ['16) Lender', 'Ujjawal Srijan'],
                ].map(([label, value], i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '5px 4px', color: '#555', width: '55%' }}>{label}</td>
                    <td style={{ padding: '5px 4px', fontWeight: 600 }}>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p style={{ marginTop: 16 }}><strong>B. Cooling-off Period</strong></p>
            <p>You may, within 3 days from availing the Loan, prepay the Loan by paying the principal and proportionate APR without any prepayment penalty.</p>

            <p><strong>C. Customer Support</strong></p>
            <p>For grievances, contact Grievance Redressal Officer:</p>
            <p>Name: Virendra Gehot | Email: admin@ujjawalsrijan.com<br />
            Address: Unit no 403, 4th Floor, MGF Metropolis Mall, MG Road, Gurgaon, Haryana-122001</p>

            <p><strong>Recovery Mechanism</strong></p>
            <p>All communications and recovery visits will be made between 8:00 AM to 7:00 PM as per RBI code of conduct.</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={closeAll}>Close</button>
          <button className="btn btn-primary" onClick={() => generatePdf('KFS.pdf')}>
            Download <FaDownload />
          </button>
        </Modal.Footer>
      </Modal>

      {/* Loan Statement Modal */}
      <Modal show={showLoanStatementModal} onHide={closeAll} backdrop="static" keyboard={false} size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16 }}>Loan Statement</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto', fontSize: 11 }}>
          <div ref={printRef} style={{ padding: 8 }}>
            <h6 style={{ fontWeight: 700 }}>JU MICRO SOLUTION FOUNDATION</h6>
            <p style={{ marginBottom: 4 }}>Address: Unit no. 403, 4th Floor, Metropolis Mall, MG Road, Gurugram, Haryana-122001</p>

            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
              <thead>
                <tr style={{ background: '#1d2e77', color: '#fff' }}>
                  <th colSpan={6} style={{ padding: 6, textAlign: 'center' }}>Customer Detail</th>
                </tr>
                <tr style={{ background: '#f0f0f0' }}>
                  {['Sr No.', 'Name', 'Type', 'Gender', 'Phone No', 'Address'].map(h => (
                    <th key={h} style={{ border: '1px solid #ddd', padding: 4 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ border: '1px solid #ddd', padding: 4 }}>1</td>
                  <td style={{ border: '1px solid #ddd', padding: 4 }}>{loanStatement?.name || ''}</td>
                  <td style={{ border: '1px solid #ddd', padding: 4 }}>Borrower</td>
                  <td style={{ border: '1px solid #ddd', padding: 4 }}>{loanStatement?.gender || ''}</td>
                  <td style={{ border: '1px solid #ddd', padding: 4 }}>{loanStatement?.mobileNo || ''}</td>
                  <td style={{ border: '1px solid #ddd', padding: 4 }}>{loanStatement?.address_line || ''}</td>
                </tr>
              </tbody>
            </table>

            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }}>
              <thead>
                <tr style={{ background: '#1d2e77', color: '#fff' }}>
                  <th colSpan={6} style={{ padding: 6, textAlign: 'center' }}>Loan Detail</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Loan Id', loanDetails?.id, 'Apply Date', fmtDate(loanStatement?.applyDate), 'Approved Date', fmtDate(loanStatement?.approvedDate)],
                  ['Branch', loanStatement?.bankBranch, 'Product Category', loanStatement?.productType, 'Product Name', loanStatement?.productName],
                  ['Loan Amount', loanStatement?.loanAmount, 'Net Disbursed', loanStatement?.disbursementAmount, 'Interest Amount', loanStatement?.interestAmount],
                  ['Processing Fee', loanStatement?.processingFees, 'Tenure (Days)', loanStatement?.tenure, 'No. of EMI', loanStatement?.noOfInstallments],
                  ['ROI', `${loanStatement?.roi || ''}%`, 'APR', `${loanDetails?.apr || ''}%`, 'Hospicash', loanDetails?.hospiCash],
                  ['Status', loanDetails?.status, 'Close Date', fmtDate(loanStatement?.closeDate), '', ''],
                ].map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (
                      <td key={j} style={{
                        border: '1px solid #ddd', padding: 4,
                        fontWeight: j % 2 === 0 ? 600 : 400,
                        background: j % 2 === 0 ? '#fafafa' : '#fff',
                      }}>
                        {cell || ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>

            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }}>
              <thead>
                <tr style={{ background: '#1d2e77', color: '#fff' }}>
                  <th colSpan={5} style={{ padding: 6, textAlign: 'center' }}>EMI Summary</th>
                </tr>
                <tr style={{ background: '#f0f0f0' }}>
                  {['', 'Total EMI', 'Mature EMI', 'Total Amount', 'Remaining'].map(h => (
                    <th key={h} style={{ border: '1px solid #ddd', padding: 4 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ border: '1px solid #ddd', padding: 4 }}>EMI</td>
                  <td style={{ border: '1px solid #ddd', padding: 4 }}>{loanStatement?.numberOfEmi || ''}</td>
                  <td style={{ border: '1px solid #ddd', padding: 4 }}>{loanStatement?.numberOfEmi || ''}</td>
                  <td style={{ border: '1px solid #ddd', padding: 4 }}>₹{loanStatement?.totalAmount || ''}</td>
                  <td style={{ border: '1px solid #ddd', padding: 4 }}>{loanStatement?.remainingAmount || ''}</td>
                </tr>
              </tbody>
            </table>

            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }}>
              <thead>
                <tr style={{ background: '#1d2e77', color: '#fff' }}>
                  <th colSpan={9} style={{ padding: 6, textAlign: 'center' }}>Repayment Details</th>
                </tr>
                <tr style={{ background: '#f0f0f0' }}>
                  {['#', 'Due Date', 'Due Amount', 'Paid Amount', 'Receipt Date', 'Collect By', 'Penalty Due', 'Penalty Paid', 'Waiver'].map(h => (
                    <th key={h} style={{ border: '1px solid #ddd', padding: 4 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {repaymentDetails?.length > 0 ? repaymentDetails.map((r, i) => (
                  <tr key={i}>
                    <td style={{ border: '1px solid #ddd', padding: 4 }}>{i + 1}</td>
                    <td style={{ border: '1px solid #ddd', padding: 4 }}>{r.dueDate || '—'}</td>
                    <td style={{ border: '1px solid #ddd', padding: 4 }}>{r.emiAmount || '—'}</td>
                    <td style={{ border: '1px solid #ddd', padding: 4 }}>{r.interestRate || '—'}</td>
                    <td style={{ border: '1px solid #ddd', padding: 4 }}>{r.principal || '—'}</td>
                    <td style={{ border: '1px solid #ddd', padding: 4 }}>{r.tenure || '—'}</td>
                    <td style={{ border: '1px solid #ddd', padding: 4 }}>{r.charges || '—'}</td>
                    <td style={{ border: '1px solid #ddd', padding: 4 }}>{r.intRateType || '—'}</td>
                    <td style={{ border: '1px solid #ddd', padding: 4 }}>{r.remainingAmount || '—'}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={9} style={{ border: '1px solid #ddd', padding: 8, textAlign: 'center' }}>No repayment data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={closeAll}>Close</button>
          <button className="btn btn-primary" onClick={() => generatePdf('Loan_statement.pdf')}>
            Download <FaDownload />
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LoanDetails;
